import dayjs from 'dayjs';
import { formatNumber } from '@/common/utils/format';

export const metricOptions = [
  { key: 'followerCount', label: '粉丝' },
  { key: 'likeCount', label: '获赞' },
  { key: 'collectCount', label: '收藏' },
  { key: 'contentCount', label: '作品' },
];

const platformMetricKeys = {
  douyin: new Set(['followerCount', 'likeCount', 'contentCount']),
  weibo: new Set(['followerCount']),
};

export const periodOptions = [
  { label: '本周', value: 'week' },
  { label: '本月', value: 'month' },
  { label: '本季', value: 'quarter' },
  { label: '本年', value: 'year' },
];

export const getMetricTitle = (key) => metricOptions.find((item) => item.key === key)?.label || key;

export const filterMetricItemsByPlatform = (metrics = [], platform) => {
  const supportedMetricKeys = platformMetricKeys[platform];
  if (!supportedMetricKeys) {
    return metrics;
  }
  return metrics.filter((metric) => supportedMetricKeys.has(metric.key));
};

export const getMetricOptionsForAccounts = (accounts = []) => {
  if (accounts.length === 0) {
    return metricOptions;
  }
  const supportedMetricKeys = new Set(
    accounts.flatMap((account) =>
      filterMetricItemsByPlatform(metricOptions, account.platform).map((item) => item.key),
    ),
  );
  return metricOptions.filter((metric) => supportedMetricKeys.has(metric.key));
};

export const formatDelta = (delta) => {
  if (delta === null || delta === undefined) {
    return '-';
  }
  const prefix = Number(delta) > 0 ? '+' : '';
  return `${prefix}${formatNumber(delta)}`;
};

export const getDeltaTextType = (delta) => {
  if (delta === null || delta === undefined || Number(delta) === 0) {
    return 'secondary';
  }
  return Number(delta) > 0 ? 'success' : 'danger';
};

export const getTopContentMetricTitle = (metric) => (metric === 'collectCount' ? '收藏' : '获赞');

export const getDefaultAnalysisRange = (today = dayjs()) => [
  today.subtract(1, 'month').startOf('month'),
  today.endOf('day'),
];

export function formatAnalysisTimestamp(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM-DD HH:mm') : '';
}

export function formatAnalysisDate(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM-DD') : '';
}

export function formatTrendPeriodTitle(value, granularity) {
  const date = formatAnalysisDate(value);
  return date && granularity === 'week' ? `${date} 所在周` : date;
}

export function getTrendTimeValue(date) {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.valueOf() : date;
}

export function buildTrendIndexSeries(series = []) {
  const accountSeries = new Map();

  series.forEach((item) => {
    const accountKey = `${item.platform}:${item.accountId}`;
    const items = accountSeries.get(accountKey) || [];
    items.push(item);
    accountSeries.set(accountKey, items);
  });

  return Array.from(accountSeries.values()).flatMap((items) => {
    const orderedItems = [...items].sort(
      (left, right) => dayjs(left.date).valueOf() - dayjs(right.date).valueOf(),
    );
    const baseValue =
      orderedItems.map((item) => Number(item.value)).find((value) => value !== 0) || 0;

    return orderedItems.map((item) => ({
      ...item,
      trendIndex: baseValue === 0 ? 0 : Math.round((Number(item.value) / baseValue) * 10000) / 100,
    }));
  });
}

export const getTrendIndexTooltipItem = ({ sampledAt, seriesName, trendIndex, value }) => {
  const sampledAtText = formatAnalysisTimestamp(sampledAt);
  return {
    name: seriesName,
    value: [
      `${formatNumber(value)}（指数 ${formatNumber(trendIndex)}）`,
      sampledAtText ? `采集于 ${sampledAtText}` : null,
    ]
      .filter(Boolean)
      .join('，'),
  };
};
